import { useIntegrationStore } from '@dashboard/modules/integration/stores/integrationStore'
import { useAuthStore } from '@dashboard/stores/authStore'
import { useConfigStore } from '@dashboard/stores/configStore'
import { useTeamStore } from '@dashboard/stores/teamStore'
import { calendarDaysUntil } from '@dashboard/utils/trialDates'
import type { User } from '@dashboard/types/user'
import { storeToRefs } from 'pinia'
import { computed, watch } from 'vue'

const toIsoString = (value: Date | string | null | undefined) =>
  value instanceof Date ? value.toISOString() : (value ?? null)

/** Calendar days left in the viewer's timezone (0 = ends today); null once ended or without a trial. */
function trialDaysLeft(owner: User | null, endsAt: string | null): number | null {
  if (!endsAt || owner?.trial_activation?.charge_now) {
    return null
  }
  const end = new Date(endsAt)
  const now = new Date()
  if (Number.isNaN(end.getTime()) || end.getTime() <= now.getTime()) {
    return null
  }
  return Math.max(0, calendarDaysUntil(end, now))
}

const APP_IDENTIFIED_EMAIL_KEY = 'rm_app_identified_email'

/**
 * tag.js `RM.push(['reset'])` flushes a pending route, refreshes derived
 * sources, and re-runs funnels and campaigns (`setupApi` in
 * resources/js/Snippet/tag.js). It does not forget subscriber identity.
 * There is no forget-identity command. `clearAllData` purges every
 * RightMessage browser value, which is broader than signing out, so logout
 * removes the subscriber keys and this marker itself.
 */
function ensureRightMessageQueue() {
  window.RM = window.RM || []
  return window.RM
}

function removeStoredSubscriberIds() {
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('rmpanda.sources.') && key.endsWith('.subscriberId')) {
      localStorage.removeItem(key)
    }
  }
}

function isAdminImpersonatingCustomer(account: User, teamPid: number | string | null) {
  // authStore has no impersonation session. recordAnalyticsEvent treats an
  // admin on a team they do not own as viewing a customer's project, using
  // user.is_admin and user.owned_teams.
  if (!account.is_admin || teamPid === null) {
    return false
  }

  return !(account.owned_teams ?? []).some((team) => String(team.pid) === String(teamPid))
}

function rememberIdentifiedEmail(email: string) {
  if (localStorage.getItem(APP_IDENTIFIED_EMAIL_KEY) === email) {
    return
  }

  ensureRightMessageQueue().push(['identifyByEmail', email])
  localStorage.setItem(APP_IDENTIFIED_EMAIL_KEY, email)
}

/**
 * Bridges webapp user/plan state to window.rm_app_user and window.rm_app_config
 * so that RightMessage tag.js flows can branch on them via YesNo JS conditions.
 *
 * Call once at the app root (e.g., MainLayout). It watches authStore.user,
 * configStore.owner, and configStore.config, and triggers RM.push(['reset'])
 * only when the published rm_app_user value changes, so flows re-evaluate
 * against fresh state without re-running on every refetch.
 */
export function useRightMessageBridge() {
  const authStore = useAuthStore()
  const configStore = useConfigStore()
  const { user } = storeToRefs(authStore)
  const { owner, config, currentTeam, currentTeamPid } = storeToRefs(configStore)
  const teamStore = useTeamStore()
  const { teams } = storeToRefs(teamStore)
  const integrationStore = useIntegrationStore()
  const integratedPartner = computed(() => integrationStore.integratedPartner)
  const integratedPartnerName = computed(() => integrationStore.integratedPartnerName)

  let resetTimer: ReturnType<typeof setTimeout> | null = null
  let publishedAppUser: string | null = null
  // The project whose config (owner and rm_app_config) is loaded; it can lag the route's PID.
  const loadedTeamPid = computed(() => currentTeam.value?.pid ?? null)

  // Expose a config summary as a window variable for RM flows to reference.
  // Uses a separate watch so config changes don't trigger a full flow reset.
  watch(
    [config, currentTeam, integratedPartner, teams],
    ([cfg, team, partner, t]) => {
      if (!cfg) {
        window.rm_app_config = null
        return
      }

      window.rm_app_config = {
        dimension_count: cfg.dimensions?.length ?? 0,
        campaign_count: cfg.campaigns?.length ?? 0,
        active_campaign_count: cfg.campaigns?.filter((c: any) => c.is_active)?.length ?? 0,
        widget_count: cfg.rightCta?.widgets?.length ?? 0,
        flow_count: cfg.rightCta?.offerFunnels?.length ?? 0,
        offer_count: cfg.rightCta?.offers?.length ?? 0,
        question_count: cfg.rightAsk?.questions?.length ?? 0,
        goal_count: cfg.goals?.length ?? 0,
        phrase_count: cfg.phrases?.length ?? 0,
        has_active_integration: !!partner,
        integration_name: integratedPartnerName.value ?? null,
        domain_count: cfg.domainNames?.length ?? 0,
        domains: cfg.domainNames ?? [],
        first_published_at: toIsoString(team?.first_published_at),
        last_published_at: toIsoString(team?.last_published_at),
        team_count: t?.length ?? 0
      }
    },
    { immediate: true }
  )

  const forgetAppIdentity = () => {
    removeStoredSubscriberIds()
    localStorage.removeItem(APP_IDENTIFIED_EMAIL_KEY)
    if (resetTimer) {
      clearTimeout(resetTimer)
      resetTimer = null
    }
    ensureRightMessageQueue().push(['reset'])
  }

  watch(
    [user, owner, currentTeamPid, loadedTeamPid],
    ([u, o, teamPid, loadedPid], previous) => {
      const previousUser = previous?.[0] ?? null
      if (!u) {
        window.rm_app_user = null
        publishedAppUser = null
        if (previousUser) {
          forgetAppIdentity()
        }
        return
      }

      // Identify when the signed-in email changes, including when an ESP link
      // already stored a subscriber id. Repeat loads of the same email no-op.
      // Skip while an admin is viewing a project they do not own.
      if (!isAdminImpersonatingCustomer(u, teamPid) && u.email) {
        rememberIdentifiedEmail(u.email)
      }

      // The owner arrives with the project's config, after the user (and the
      // route's team PID). Publishing before it would run flows once against
      // default plan data and again when it lands.
      if (o === undefined) {
        return
      }

      // The project owner's trial deadline: fixed-end trial, then renewing trial, then a generic trial.
      const trialEndsAt =
        o?.trial_activation?.trial_ends_at ?? o?.renewal_plan?.starts_at ?? toIsoString(o?.trial_ends_at)
      const appUser = {
        email: u.email,
        name: u.name,
        is_admin: u.is_admin,
        is_agency: u.is_agency,
        free_account: u.free_account,
        on_trial: o?.on_trial ?? false,
        trial_ends_at: trialEndsAt,
        is_non_renewing_trial: !!o?.trial_activation,
        trial_days_left: trialDaysLeft(o, trialEndsAt),
        renewal_enabled: !o?.trial_activation && !!o?.renewal_plan,
        plan_tier: o?.plan?.tier ?? 'grow',
        plan_features: o?.plan?.features ?? [],
        billing_model: o?.billing_model ?? 'per_team',
        is_free_builder: o?.is_free_builder ?? false,
        stripe_status: o?.stripe_status ?? null,
        has_payment_method: o?.has_payment_method ?? false,
        stripe_id: o?.stripe_id ?? null,
        created_at: o?.created_at ?? null,
        pricing_version: o?.pricing_version ?? null,
        active_subscription:
          o?.active_subscription?.stripe_status && o.active_subscription.created_at
            ? {
                stripe_status: o.active_subscription.stripe_status,
                created_at: o.active_subscription.created_at
              }
            : null,
        owned_team_count: o?.owned_teams?.length ?? 0
      }
      window.rm_app_user = appUser

      // Every config refetch (saves, publishes, remote edits) replaces the
      // owner object. Re-run flows only when the data they read changed:
      // this user value, or the project whose rm_app_config is loaded.
      const serialized = JSON.stringify([appUser, loadedPid])
      if (serialized === publishedAppUser) {
        return
      }
      publishedAppUser = serialized

      // Debounce the reset so rapid user/owner updates (from separate API
      // responses resolving in different ticks) only trigger one flow run
      if (resetTimer) {
        clearTimeout(resetTimer)
      }
      resetTimer = setTimeout(() => {
        ensureRightMessageQueue().push(['reset'])
        resetTimer = null
      }, 50)
    },
    { immediate: true }
  )
}
